import React, { useState } from "react";
import { useLanguage, T, pickBilingual } from "@/lib/i18n";

function formatDate(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "bn" ? "bn-IN" : "en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

export default function NewsList() {
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);

  // Facebook video to embed in Latest News
  const fbVideoUrl = "https://www.facebook.com/bhaskar.ghosh.551982/videos/2119164692209954/";
  const fbEmbedSrc = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(fbVideoUrl)}&show_text=false&width=560`;
  const announcementItems = [
    { id: "rakhibandhan", date: T.announcements.rakhibandhan.timestamp, title: T.announcements.rakhibandhan.title[lang], body: T.announcements.rakhibandhan.body[lang] },
    { id: "food-campus", date: T.announcements.foodCampus.timestamp, title: T.announcements.foodCampus.title[lang], body: T.announcements.foodCampus.body[lang], link: "https://www.facebook.com/share/p/14oEqBGH8yK/" },
  ];

  return (
    <section id="news" data-testid="news-section" className="bg-white border-t border-sjm-line">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 lg:py-32">
        <div className="mb-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-sjm-red">{T.news.eyebrow[lang]}</div>
          <h2 className={`mt-3 font-display font-extrabold tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-sjm-ink ${lang === "bn" ? "font-bn" : ""}`}>
            {T.news.title[lang]}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {items.length > 0 && (
            <article data-testid="news-feature" className="lg:col-span-8 group border border-sjm-line hover-lift overflow-hidden">
              {items[0].image_url && (
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={items[0].image_url} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                </div>
              )}
              <div className="p-8">
                <div className="text-xs uppercase tracking-wider text-sjm-muted mb-3">
                  {formatDate(items[0].published_at, lang)}
                </div>
                <h3 className={`font-display font-extrabold text-2xl sm:text-3xl text-sjm-ink leading-tight ${lang === "bn" ? "font-bn" : ""}`}>
                  {pickBilingual(items[0], "title", lang)}
                </h3>
                <p className={`mt-4 text-sjm-muted leading-relaxed ${lang === "bn" ? "font-bn" : ""}`}>
                  {pickBilingual(items[0], "excerpt", lang)}
                </p>
              </div>
            </article>
          )}

          <div className="lg:col-span-4 flex flex-col gap-6">
            <div className="group border border-sjm-line hover-lift overflow-hidden bg-white">
              <div className="p-4">
                <div className="text-xs uppercase tracking-wider text-sjm-muted mb-3">{lang === "bn" ? "ভিডিও" : "Video"}</div>
                <div className="w-full">
                  <iframe src={fbEmbedSrc} title="Facebook video" className="w-full" style={{height: 640, border:0}} frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" allowFullScreen></iframe>
                </div>
              </div>
            </div>

            {(Array.isArray(items) ? items.slice(1, 4) : []).map((n, i) => (
              <article
                key={n.id}
                data-testid={`news-card-${i}`}
                className="group border border-sjm-line hover-lift p-6"
              >
                <div className="text-xs uppercase tracking-wider text-sjm-muted mb-2">
                  {formatDate(n.published_at, lang)}
                </div>
                <h3 className={`font-display font-bold text-lg text-sjm-ink leading-snug ${lang === "bn" ? "font-bn" : ""}`}>
                  {pickBilingual(n, "title", lang)}
                </h3>
                <p className={`mt-2 text-sm text-sjm-muted line-clamp-2 ${lang === "bn" ? "font-bn" : ""}`}>
                  {pickBilingual(n, "excerpt", lang)}
                </p>
              </article>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6">
          {announcementItems.map((announcement) => (
            <article key={announcement.id} className="border border-sjm-line bg-slate-50 p-6 sm:p-8">
              <div className="text-xs uppercase tracking-wider text-sjm-muted mb-3">
                {formatDate(announcement.date, lang)}
              </div>
              <h3 className={`font-display font-bold text-xl sm:text-2xl text-sjm-ink leading-tight ${lang === "bn" ? "font-bn" : ""}`}>
                {announcement.title}
              </h3>
              <p className={`mt-4 text-sjm-muted leading-relaxed whitespace-pre-line ${lang === "bn" ? "font-bn" : ""}`}>
                {announcement.body}
              </p>
              {announcement.link && (
                <div className="mt-5">
                  <a
                    href={announcement.link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center rounded-full bg-sjm-red px-4 py-2 text-sm font-semibold text-white hover:bg-[#b91c1c]"
                  >
                    {T.announcements.facebook.label[lang]}
                  </a>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
